import React, { useEffect, useReducer } from 'react';
import { first, isEmpty, isEqual, isNil } from 'lodash';
import { BodyScrollEvent, IRowNode } from '@ag-grid-community/core';

import { InputSuggest } from 'src/common-ui/index';
import type { Suggestion } from 'src/common-ui/components/Inputs/InputSuggest/InputSuggest';

import {
  getListDataValidValues,
  getValidValues,
} from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/StyleEditSection.client';
import { getUrl } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';
import { ValidValuesEditorProps, ValidValuesEditorState } from './ValidValuesEditor';
import { ClientDataApi, ListDataApi } from 'configuration-schemas/confdefnView';
import { EditableGridHeaderEditorBaseProps } from './TextValidationHeaderEditor';
import { Tooltip } from '@material-ui/core';
import {
  MISMATCHED_OPTIONS_TEXT,
  ROW_SELECTION_REQUIRED_TEXT,
  ROW_SELECTIONS_UPDATING_TEXT,
} from 'src/utils/Domain/Constants';
import { style } from 'typestyle';

interface ValidValuesHeaderEditorProps extends ValidValuesEditorProps, EditableGridHeaderEditorBaseProps {
  getDataConfig: (row: IRowNode, configuredApi: ClientDataApi) => ClientDataApi;
  /** Using unprocessed dataApi for header editors instead because
   * during column header generation, there are no selected rows to populate dataApi params */
  unprocessedDataConfig: ClientDataApi;
}

// Local component reducer actions
type UpdateMenuVisibility = {
  type: 'updateMenuVisibility';
  payload: {
    showMenu: boolean;
  };
};
type SetupSingleSelect = {
  type: 'setupSingleSelect';
  payload: {
    loaded: boolean;
    validValues: Suggestion[];
    selectedOption: Suggestion | undefined;
    useLabelAsValue: boolean;
  };
};
type SetupMultiSelect = {
  type: 'setupMultiSelect';
  payload: {
    loaded: boolean;
    validValues: Suggestion[];
    selectedOptions: Suggestion[];
  };
};
type UpdateSelection = {
  type: 'updateSelection';
  payload: {
    showMenu: boolean;
    selectedOption: Suggestion;
  };
};
type UpdateMultiSelection = {
  type: 'updateMultiSelection';
  payload: {
    showMenu: boolean;
    selectedOptions: Suggestion[];
  };
};
type UpdateDataConfigs = {
  type: 'updateDataConfigs';
  payload: {
    dataConfigs: ClientDataApi[];
    dataConfigParamsMatch: boolean;
  };
};
type ValidValuesHeaderEditorActions =
  | SetupSingleSelect
  | SetupMultiSelect
  | UpdateSelection
  | UpdateMultiSelection
  | UpdateDataConfigs
  | UpdateMenuVisibility;

interface ValidValuesHeaderEditorState extends ValidValuesEditorState {
  showMenu: boolean;
  dataConfigs: (ClientDataApi | ListDataApi)[];
  dataConfigParamsMatch: boolean;
}

function componentReducer(
  state: ValidValuesHeaderEditorState,
  { type, payload }: ValidValuesHeaderEditorActions,
): ValidValuesHeaderEditorState {
  switch (type) {
    case 'setupSingleSelect': {
      const { loaded, validValues, selectedOption, useLabelAsValue } = payload;
      return {
        ...state,
        loaded,
        selectedOption,
        useLabelAsValue,
        validValues,
        valueChosen: false,
      };
    }
    case 'setupMultiSelect': {
      const { loaded, validValues, selectedOptions } = payload;
      return {
        ...state,
        loaded,
        selectedOptions,
        validValues,
        valueChosen: false,
      };
    }
    case 'updateSelection': {
      const { selectedOption, showMenu } = payload;
      return {
        ...state,
        valueChosen: true,
        selectedOption,
        showMenu,
      };
    }
    case 'updateMultiSelection': {
      const { selectedOptions, showMenu } = payload;
      return {
        ...state,
        valueChosen: true,
        selectedOptions,
        showMenu,
      };
    }
    case 'updateDataConfigs': {
      const { dataConfigs, dataConfigParamsMatch } = payload;
      return {
        ...state,
        dataConfigs,
        dataConfigParamsMatch,
      };
    }
    case 'updateMenuVisibility': {
      const { showMenu } = payload;
      return {
        ...state,
        showMenu,
      };
    }
    default:
      return state;
  }
}
export type SingleOrMultiRef = Suggestion;
/**
 * Verifies that all items selected have the same valid values options
 *
 * @param dataConfigs The currently selected grid items processed dataApis
 * @returns boolean
 */
export function selectedItemsOptionsMatch(dataConfigs: (ClientDataApi | ListDataApi)[]): boolean {
  if (isEmpty(dataConfigs) || dataConfigs.length === 1) {
    return true;
  }

  const firstItem = first(dataConfigs);
  // compare first item params with every other items params, beginning with second item in array
  const allItemsParamsMatch = dataConfigs.slice(1).every((dc) => isEqual(firstItem?.params, dc.params));
  return allItemsParamsMatch;
}

// TODO: setting `injectFirst={true}` will probably fix the need for all these importants
const overrides = {
  autocomplete: {
    root: style({
      $debugName: 'valid-values-headitor-root-overrides',
      height: 'var(--s5-headitor-input-height)',
      margin: 'var(--s5-headitor-input-horizontal-spacing)',
    }),
    inputRoot: style({
      $debugName: 'valid-values-headitor-inputroot-overrides',
      height: 'var(--s5-headitor-input-height)',
      // paddingRight is set via css cascade and don't want to explicitly override
      paddingTop: '0 !important',
      paddingBottom: '0 !important',
      paddingLeft: '0 !important',
      width: 'unset !important',
    }),
    input: style({
      $debugName: 'valid-values-headitor-input-overrides',
      // because of the shorter headitor row, lowering the amnount of padding
      // keeps the input for the textfield within the dropdown container
      padding: '4px !important',
    }),
  },
};

export const ValidValuesHeaderEditor = (props: ValidValuesHeaderEditorProps) => {
  const {
    allowEmptyOption,
    api,
    clientHandler,
    column,
    concatOptionValues,
    dataQa,
    getDataConfig,
    ignoreCache,
    includeCurrent,
    multiSelect,
    onApplyEdit,
    options,
    getSelectedItems,
    getCellsUpdating,
    returnSelectionObject,
    unprocessedDataConfig,
    value,
  } = props;

  const [state, dispatch] = useReducer(componentReducer, {
    dataConfigs: [],
    dataConfigParamsMatch: false,
    loaded: false,
    selectedOption: undefined,
    selectedOptions: [],
    showMenu: false,
    useLabelAsValue: false,
    validValues: [],
    valueChosen: false,
    showConfirm: false,
    pendingSelection: undefined,
    showAddNew: false,
  });

  const {
    dataConfigs,
    dataConfigParamsMatch,
    selectedOption,
    loaded,
    selectedOptions,
    showMenu,
    useLabelAsValue,
    validValues,
  } = state;

  const formatSelection = (selection: Suggestion) => {
    if (!isNil(returnSelectionObject) && returnSelectionObject) {
      return selection;
    }
    return !useLabelAsValue ? selection.value : selection.label;
  };

  const formatSelections = (selections: Suggestion[]) => {
    return !isEmpty(selections)
      ? selections.map((option: Suggestion) => (!useLabelAsValue ? option.value : option.label))
      : [];
  };
  const cancelSelection = () => {
    if (multiSelect === true) {
      dispatch({
        type: 'setupMultiSelect',
        payload: { loaded: state.loaded, validValues, selectedOptions: [] },
      });
    } else {
      dispatch({
        type: 'setupSingleSelect',
        payload: { loaded: state.loaded, validValues, selectedOption: undefined, useLabelAsValue },
      });
    }
    dispatch({ type: 'updateMenuVisibility', payload: { showMenu: false } });
  };

  const onInputSuggestBlur = () => cancelSelection();
  const onInputSuggestOpen = () => dispatch({ type: 'updateMenuVisibility', payload: { showMenu: true } });

  useEffect(() => {
    function bodyScrollEventListener(event: BodyScrollEvent) {
      if (event.direction === 'horizontal') {
        dispatch({ type: 'updateMenuVisibility', payload: { showMenu: false } });
      }
    }
    api.addEventListener('bodyScroll', bodyScrollEventListener);
    return () => api.removeEventListener('bodyScroll', bodyScrollEventListener);
  }, [api]);

  useEffect(() => {
    if (isEmpty(dataConfigs) || !dataConfigParamsMatch) {
      return;
    }

    // at this point we know all dataConfigs params match, safe to retrieve validValues

    const dataConfig = first(dataConfigs);
    if (isNil(dataConfig)) {
      return;
    }
    const insertEmptyOption = multiSelect ? false : allowEmptyOption;
    const fetchProm = dataConfig?.isListData
      ? getListDataValidValues(dataConfig)
      : getValidValues(getUrl(dataConfig), insertEmptyOption, concatOptionValues, ignoreCache);
    fetchProm
      .then((validValues: Suggestion[]) => {
        if (isNil(multiSelect) || multiSelect == false) {
          dispatch({
            type: 'setupSingleSelect',
            payload: {
              validValues,
              loaded: true,
              selectedOption: undefined,
              useLabelAsValue,
            },
          });
        } else {
          dispatch({
            type: 'setupMultiSelect',
            payload: {
              validValues,
              loaded: true,
              selectedOptions: [],
            },
          });
        }
      })
      .catch((err) => console.log(err));
  }, [
    allowEmptyOption,
    clientHandler,
    concatOptionValues,
    dataConfigs,
    dataConfigParamsMatch,
    ignoreCache,
    includeCurrent,
    multiSelect,
    options,
    value,
    useLabelAsValue,
  ]);

  useEffect(() => {
    const selectedItemsDataConfigs = getSelectedItems().map((si) => getDataConfig(si, unprocessedDataConfig));
    const dataConfigParamsMatch = selectedItemsOptionsMatch(selectedItemsDataConfigs);

    dispatch({
      type: 'updateDataConfigs',
      payload: {
        dataConfigs: selectedItemsDataConfigs,
        dataConfigParamsMatch,
      },
    });
  }, [getDataConfig, getSelectedItems, unprocessedDataConfig]);

  const onSelect = (selection: Suggestion) => {
    dispatch({
      type: 'updateSelection',
      payload: { selectedOption: selection, showMenu: false },
    });
  };

  const onMultiSelect = (selections: Suggestion[]) => {
    dispatch({
      type: 'updateMultiSelection',
      payload: { selectedOptions: selections, showMenu: true },
    });
  };

  const onSingleSelection = (selection: Suggestion) => {
    onSelect(selection);
    const items = getSelectedItems();
    const formattedValue = formatSelection(selection);
    onApplyEdit(formattedValue, items, column.getColDef());
  };

  const onMultiSelection = (selections: Suggestion[]) => {
    onMultiSelect(selections);
  };

  const onSubmit = () => {
    if (!state.valueChosen) {
      dispatch({ type: 'updateMenuVisibility', payload: { showMenu: false } });
      return;
    }

    const formattedValue = multiSelect === true ? formatSelections(selectedOptions) : formatSelection(selectedOption!);
    onApplyEdit(formattedValue, getSelectedItems(), column.getColDef());
    cancelSelection();
  };

  // determine options, selection(s), and handler based on single or multi select
  const isMultiSelect = multiSelect === true;
  const onSelectHandler = isMultiSelect ? undefined : onSingleSelection;
  const onMultiSelectHandler = isMultiSelect ? onMultiSelection : undefined;
  const selected = isMultiSelect ? selectedOptions : selectedOption;

  const inputOptions: Suggestion[] = validValues;
  const noSelections = isEmpty(getSelectedItems());
  const isUpdating = getCellsUpdating().length > 0;
  const mismatchedSelectionsOptions = dataConfigParamsMatch === false;
  const disabled = noSelections || mismatchedSelectionsOptions || isUpdating;
  const menuIsOpen = !disabled && showMenu;

  let title = '';
  if (noSelections) {
    title = ROW_SELECTION_REQUIRED_TEXT;
  } else if (isUpdating) {
    title = ROW_SELECTIONS_UPDATING_TEXT;
  } else if (mismatchedSelectionsOptions) {
    title = MISMATCHED_OPTIONS_TEXT;
  }

  return (
    <Tooltip title={title}>
      {/* This div is load bearing, if removed tooltips will no longer appear on headitors */}
      <div>
        <InputSuggest
          classes={overrides}
          validValues={inputOptions}
          multiSelect={multiSelect}
          selected={selected}
          onSelect={onSelectHandler}
          onMultiSelect={onMultiSelectHandler}
          dataQa={dataQa}
          disabled={disabled}
          onBlur={onInputSuggestBlur}
          open={menuIsOpen}
          onOpen={onInputSuggestOpen}
          // do not want headitors to have the menu open by default
          defaultOpenOnFocus={false}
          placeholder=""
          allowPortal={true}
          popperWidth={column.getActualWidth() + 120}
          isLoading={!disabled && !loaded}
          onCancel={cancelSelection}
          onSubmit={onSubmit}
        />
      </div>
    </Tooltip>
  );
};
